import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import { ReactComponentContainer } from "@/core/types/types";
import { useEffect } from "react";
import { AnimationProvider } from "@/core/contexts/AnimationContext/AnimationContext";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    ctaContained: true;
  }
}

const filsonBold: any = {
  fontFamily: "FilsonProBold",
  fontStyle: "normal",
  fontDisplay: "swap",
  fontWeight: 700,
  src: `url('fonts/filson/FilsonProBold.otf') format('opentype')`,
};

let theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 425,
      md: 1024,
      lg: 1440,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: "#18901C",
      light: "#28a228",
    },
    secondary: {
      main: "#000",
      light: "#f3f5f8",
    },
    layout: {
      background: "#333",
      color: "#787b7d",
    },
    background: {
      default: "#fff",
      paper: "#fff",
    },
    text: {
      primary: "#000",
      secondary: "#333",
    },
    grey: {
      800: "#666666",
      A100: "#787b7d",
      A200: "#333333",
      A400: "#888888",
      A700: "#DDDDDD",
    },
    error: {
      main: "#B3261E",
    },
    warning: {
      main: "#ff9800",
    },
    success: {
      main: "#28a228",
    },
  },
  typography: {
    fontFamily: "Open-sans, sans-serif",
    h1: {
      fontSize: "2rem",
      fontFamily: "FilsonProBold, sans-serif",
      fontWeight: "700 !important",
    },
    h2: {
      fontSize: "1.5rem",
      fontFamily: "FilsonProBold, sans-serif",
      fontWeight: "700 !important",
    },
    h3: {
      fontSize: "16px",
      fontWeight: 700,
    },
    subtitle1: {
      fontSize: "15px",
      fontWeight: 700,
    },
    body1: {
      fontSize: "16px",
      fontWeight: "inherit",
    },
    body2: {
      fontSize: "14px",
      fontWeight: "inherit",
    },
  },
});

theme = createTheme(theme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "@font-face": [filsonBold],
        ".fonts-loading": {
          "h1, h2": {
            opacity: 0,
          },
        },
        ".fonts-loaded": {
          "h1, h2": {
            opacity: 1,
            transition: "opacity 0.2s ease-in",
          },
        },
        body: {
          fontSize: "0.875rem",
          lineHeight: 1.43,
          letterSpacing: "0.01071em",
        },
        "h1, h2": {
          fontFamily: "FilsonProBold, sans-serif",
        },
        h1: {
          minHeight: "2.5rem",
        },
        h2: {
          minHeight: "2rem",
        },
        ".MuiContainer-root": {
          maxWidth: "960px",
        },
        ".MuiButton-root.navigation": {
          [theme.breakpoints.down("md")]: {
            width: "100%",
          },
          padding: "16px 32px",
          boxShadow: "none",
          height: "40px",
          textTransform: "inherit",
          "& svg path": {
            fill: "currentColor",
          },
        },
        ".MuiInputBase-root": {
          backgroundColor: theme.palette.background.default,
          margin: "0 auto",
          borderRadius: "4px",
          width: "100%",
        },
        ".MuiTextField-root": {
          width: "100%",
        },
        ".MuiOutlinedInput-root": {
          "& fieldset": {
            border: `1px solid ${theme.palette.grey.A200}`,
          },
          "&:not(.Mui-error):not(.Mui-disabled):hover fieldset": {
            borderColor: `${theme.palette.primary.main} !important`,
          },
        },
        ".MuiOutlinedInput-root, .MuiTextField-root, .MuiInputBase-root ": {
          "&:has(> input:-webkit-autofill)": {
            "-webkit-text-fill-color": theme.palette.secondary.main,
            fontWeight: 600,
            boxShadow: "inset 0 0 30px 30px rgb(232, 240, 254)",
            "& :focus, & :hover, & :active, ": {
              boxShadow: "inset 0 0 30px 30px rgb(232, 240, 254)",
            },
          },
        },
        ".MuiFormLabel-root.MuiInputLabel-root:not(.Mui-error), .MuiInputBase-root input::placeholder, .MuiFormControlLabel-root":
          {
            color: theme.palette.grey.A200,
            opacity: 1,
          },
        ".MuiFormControlLabel-root": {
          borderRadius: "4px",
          margin: "0",
          paddingRight: "16px",
          background: theme.palette.background.default,
          border: `1px solid ${theme.palette.grey.A200}`,
          "&:hover": {
            border: `1px solid ${theme.palette.primary.main}`,
          },
        },
        ".MuiFormControlLabel-root.MuiFormControlLabel-labelPlacementEnd": {
          margin: 0,
        },
        ".MuiFormControlLabel-root:has(.Mui-checked)": {
          border: `2px solid ${theme.palette.primary.main}`,
        },
        ".MuiFormControlLabel-root .MuiSvgIcon-root": {
          width: "12px",
          height: "12px",
        },
        ".MuiButton-root": {
          "&.MuiButton-colorInherit.navigation": {
            color: theme.palette.grey.A100,
            "&:hover": {
              color: theme.palette.background.paper,
            },
          },
        },
        ".MuiOutlinedInput-root.no-value .MuiInputBase-input::placeholder": {
          color: "rgba(0, 0, 0, 0.60) !important",
        },
        ".autosuggest-icon-wrapper > div": {
          fill: "rgba(0, 0, 0, 0.38)",
          backgroundColor: "rgba(0, 0, 0, 0.38) !important",
          width: "20px !important",
          height: "20px !important",
        },
        ".MuiOutlinedInput-root.Mui-focused .autosuggest-icon-wrapper > div, .MuiOutlinedInput-root.Mui-focused .MuiFormLabel-root":
          {
            fill: `${theme.palette.primary.main} !important`,
            color: theme.palette.primary.main,
            backgroundColor: `${theme.palette.primary.main} !important`,
          },
        ".MuiOutlinedInput-root.Mui-error .autosuggest-icon-wrapper > div, .MuiOutlinedInput-root.Mui-error .MuiFormLabel-root":
          {
            fill: `${theme.palette.error.main} !important`,
            color: theme.palette.error.main,
            backgroundColor: `${theme.palette.error.main} !important`,
          },
        ".MuiOutlinedInput-root:not(.Mui-focused).has-value:not(.Mui-error) .autosuggest-icon-wrapper > div":
          {
            fill: "rgba(0, 0, 0, 0.6)",
            backgroundColor: "rgba(0, 0, 0, 0.6) !important",
          },
        ".MuiOutlinedInput-root.has-value:not(.Mui-error):not(.Mui-focused) .autosuggest-icon-wrapper > div":
          {
            fill: "rgba(0, 0, 0, 0.6)",
            backgroundColor: "rgba(0, 0, 0, 0.6) !important",
          },
        ".MuiFormLabel-root.Mui-focused:not(.Mui-error)": {
          color: `${theme.palette.primary.main} !important`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "16px",
        },
      },
      variants: [
        {
          props: { variant: "ctaContained" },
          style: {
            color: "#fff",
            backgroundColor: "#333",
            textTransform: "none",
            borderRadius: "3px",
            transition: "unset",
            "&:hover": {
              backgroundColor: "#111",
            },
          },
        },
      ],
    },
  },
});

const FlightrightTheme = ({ children }: ReactComponentContainer) => {
  useEffect(() => {
    const font = new FontFace(
      "FilsonProBold",
      `url('fonts/filson/FilsonProBold.otf') format('opentype')`,
      { weight: "700" }
    );

    document.body.classList.add("fonts-loading");

    font
      .load()
      .then(() => {
        (document.fonts as any).add(font);

        document.body.classList.remove("fonts-loading");
        document.body.classList.add("fonts-loaded");
      })
      .catch((err) => {
        console.error("Font loading failed:", err);
        document.body.classList.remove("fonts-loading");
      });

    return () => {
      document.body.classList.remove("fonts-loading", "fonts-loaded");
    };
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <AnimationProvider files={["flightright/airplane-window-default"]}>
          {children}
        </AnimationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default FlightrightTheme;
